import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Trophy, Flame, ArrowLeft, Medal, Crown, Sparkles } from 'lucide-react';

export default function Leaderboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState({ leaders: [], you: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/games/leaderboard?period=${period}`).then(r => setData(r.data || { leaders: [], you: null })).finally(() => setLoading(false));
  }, [period]);

  const leaders = data.leaders || [];
  const you = data.you;

  return (
    <div className="bg-white min-h-screen">
      <section className="relative bg-ink text-white py-12 md:py-16 overflow-hidden" data-testid="leaderboard-hero">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 400px at 75% 50%, rgba(212,164,55,0.18), transparent 60%)' }} />
        <div className="relative kc-container">
          <Link to="/games" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/60 hover:text-[color:var(--kc-gold)]" data-testid="lb-back"><ArrowLeft size={12} /> Games</Link>
          <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">
                <Sparkles size={12} /> The Leaderboard
              </div>
              <h1 className="mt-3 font-display uppercase leading-[0.95]" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4rem)' }}>
                WHO'S DOING <br /><span className="text-[color:var(--kc-gold)]">THE WORK?</span>
              </h1>
              <p className="mt-3 text-white/75 max-w-xl">Earn XP by playing games, finishing lessons, and journaling. Climb the ladder. First name only — no last names, no shame.</p>
            </div>

            <div className="inline-flex rounded-full p-1 bg-white/5 border border-white/10" data-testid="lb-period">
              {[
                { k: 'week', label: 'This Week' },
                { k: 'all',  label: 'All-Time'  },
              ].map(o => (
                <button
                  key={o.k}
                  onClick={() => setPeriod(o.k)}
                  className={`px-4 h-9 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${period === o.k ? 'bg-[color:var(--kc-gold)] text-black' : 'text-white/70 hover:text-white'}`}
                  data-testid={`lb-period-${o.k}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-white">
        <div className="kc-container max-w-3xl">
          {you && (
            <div className="mb-6 p-4 rounded-[var(--kc-radius-md)] border-2 border-[color:var(--kc-gold)] bg-gold-soft flex items-center justify-between gap-4" data-testid="lb-you">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-ink text-[color:var(--kc-gold)] font-display text-xl flex items-center justify-center">#{you.rank}</div>
                <div>
                  <div className="font-display uppercase text-xl leading-tight">YOU — {you.name}</div>
                  <div className="text-xs text-ink-2 mt-0.5">Level {you.level} · {you.streak}d streak</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-3xl text-[color:var(--kc-gold-2)] tabular-nums" data-testid="lb-you-xp">{you.xp}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink-2">XP</div>
              </div>
            </div>
          )}

          {loading && (
            <div className="py-12 text-center text-muted-kc" data-testid="lb-loading">Loading leaderboard\u2026</div>
          )}

          {!loading && leaders.length === 0 && (
            <div className="py-12 text-center bg-surface-2 rounded-[var(--kc-radius-md)] border border-kc" data-testid="lb-empty">
              <Trophy className="mx-auto text-muted-kc mb-3" size={36} />
              <h3 className="font-display uppercase text-2xl">No one's on the board yet.</h3>
              <p className="mt-2 text-muted-kc">Be the first. Play a game or finish a lesson to claim #1.</p>
              {user ? (
                <Link to="/games" className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold" data-testid="lb-empty-cta">Play a game</Link>
              ) : (
                <Link to="/signup" className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold">Sign up to compete</Link>
              )}
            </div>
          )}

          {!loading && leaders.length > 0 && (
            <ol className="divide-y divide-kc bg-white rounded-[var(--kc-radius-md)] border border-kc overflow-hidden" data-testid="lb-list">
              {leaders.map((p) => {
                const isTop = p.rank <= 3;
                const Icon = p.rank === 1 ? Crown : p.rank === 2 ? Trophy : p.rank === 3 ? Medal : null;
                return (
                  <li
                    key={p.user_id}
                    className={`flex items-center gap-3 px-4 py-3 ${p.is_you ? 'bg-gold-soft' : isTop ? 'bg-surface-2' : 'bg-white'}`}
                    data-testid={`lb-row-${p.rank}`}
                  >
                    <div className={`w-10 text-center font-display text-xl tabular-nums ${isTop ? 'text-[color:var(--kc-gold-2)]' : 'text-ink-2'}`}>#{p.rank}</div>
                    <div className={`w-10 h-10 rounded-full font-display text-lg flex items-center justify-center flex-shrink-0 ${isTop ? 'bg-ink text-[color:var(--kc-gold)]' : 'bg-surface-2 text-ink'}`}>
                      {Icon ? <Icon size={18} className="text-[color:var(--kc-gold)]" /> : p.initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ink truncate">{p.name}{p.is_you && <span className="ml-2 text-[10px] uppercase tracking-wider text-[color:var(--kc-gold-2)]">You</span>}</div>
                      <div className="text-xs text-ink-2 flex items-center gap-2 mt-0.5">
                        <span>Lvl {p.level}</span>
                        <span className="text-muted-kc">·</span>
                        <span className="inline-flex items-center gap-1"><Flame size={11} className="text-[color:var(--kc-gold)]" /> {p.streak}d</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-display text-2xl text-[color:var(--kc-gold-2)] tabular-nums">{p.xp}</div>
                      <div className="text-[9px] uppercase tracking-wider text-muted-kc">XP</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
            <div className="p-4 rounded-[var(--kc-radius-md)] border border-kc bg-white">
              <div className="text-[10px] uppercase tracking-wider text-muted-kc">Game session</div>
              <div className="font-display text-2xl text-[color:var(--kc-gold-2)]">+10–100 XP</div>
            </div>
            <div className="p-4 rounded-[var(--kc-radius-md)] border border-kc bg-white">
              <div className="text-[10px] uppercase tracking-wider text-muted-kc">Finish a lesson</div>
              <div className="font-display text-2xl text-[color:var(--kc-gold-2)]">+25 XP</div>
            </div>
            <div className="p-4 rounded-[var(--kc-radius-md)] border border-kc bg-white">
              <div className="text-[10px] uppercase tracking-wider text-muted-kc">Journal entry</div>
              <div className="font-display text-2xl text-[color:var(--kc-gold-2)]">+5 XP</div>
            </div>
          </div>
          <p className="mt-3 text-center text-xs text-muted-kc">Hit a 7-day streak → +50 XP bonus. 30-day streak → +200 XP bonus.</p>
        </div>
      </section>
    </div>
  );
}
